import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuthStore } from '@/stores/authStore';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { setSentryUser, clearSentryUser } from '@/lib/sentry';

export function useAuth() {
  const { user, session, isLoading, setSession, setIsLoading } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setIsLoading(false);

        if (event === 'SIGNED_IN' && session?.user) {
          // Set Sentry user context
          setSentryUser({
            id: session.user.id,
            email: session.user.email,
          });
          
          // Check if there's a redirect URL in the query params or localStorage
          const urlParams = new URLSearchParams(window.location.search);
          const redirectParam = urlParams.get('redirect');
          const pendingDownload = localStorage.getItem('pending_qr_download');
          
          if (redirectParam) {
            // User was redirected from a specific page (like /create)
            navigate(redirectParam);
          } else if (pendingDownload && window.location.pathname === '/auth') {
            // User was trying to download a QR, redirect back to create page
            navigate('/create');
          } else {
            // Only redirect to dashboard if we're on the auth page or home page
            const publicPaths = ['/auth', '/'];
            if (publicPaths.includes(window.location.pathname)) {
              navigate('/dashboard');
            }
          }
        } else if (event === 'SIGNED_OUT') {
          // Clear Sentry user context
          clearSentryUser();
          navigate('/');
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setIsLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [setSession, setIsLoading, navigate]);

  const signUp = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: window.location.origin,
          // Auto-confirm email (requires Supabase Dashboard setting)
          data: {
            email_confirmed: true
          }
        },
      });
      
      if (error) {
        console.error('Signup error:', error);
        toast.error(`Signup failed: ${error.message}`);
        return { error };
      }
      
      console.log('Signup success:', data);
      
      // Check if user is automatically signed in
      if (data.session) {
        // User is signed in immediately (email confirmation disabled)
        toast.success('Welcome to NexQR! 🎉 Check your email for important account details.');
        // The auth state listener will handle navigation to dashboard
      } else {
        // Email confirmation required (shouldn't happen with our setup)
        toast.success('Account created! Please check your email to activate your account.');
      }
      
      return { error: null };
    } catch (err: any) {
      console.error('Signup exception:', err);
      toast.error(`Signup failed: ${err.message || 'Unknown error'}`);
      return { error: err };
    }
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) {
      toast.error(error.message);
      return { error };
    }
    
    toast.success('Welcome back!');
    return { error: null };
  };

  const signInWithGoogle = async () => {
    // Check if there's a redirect URL in the query params or localStorage
    const urlParams = new URLSearchParams(window.location.search);
    const redirectParam = urlParams.get('redirect');
    const pendingDownload = localStorage.getItem('pending_qr_download');
    
    // Determine the redirect URL based on context
    let redirectUrl = `${window.location.origin}/dashboard`;
    
    if (redirectParam) {
      // User was redirected from a specific page (like /create)
      redirectUrl = `${window.location.origin}${redirectParam}`;
    } else if (pendingDownload) {
      // User was trying to download a QR, redirect back to create page
      redirectUrl = `${window.location.origin}/create`;
    }
    
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectUrl,
      },
    });
    
    if (error) {
      toast.error(error.message);
    }
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast.error(error.message);
    } else {
      toast.success('Signed out successfully');
    }
  };

  return {
    user,
    session,
    isLoading,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
  };
}
